import { useFormUnsavedChangesWarning } from "@/hooks/useFormUnsavedChangesWarning";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { DefaultValues, useForm } from "react-hook-form";
import { memo, useEffect } from "react";
import {
  writeRoleBasedProjectRates,
  WriteRoleBasedProjectRates,
} from "@/schemas/roleBasedProjectRates";
import Form from "@/components/Form";
import { RoleBasedProjectRatesService } from "@/services/roleBasedProjectRatesService";
import PageHeader from "@/components/PageHeader";
import { useProjectRateBreadcrumbs } from "@/modules/projects/components/rates/hooks/useProjectRateBreadcrumbs";
import { ProjectDetailsAndNavigation } from "@/modules/projects/components/ProjectDetailsAndNavigation";
import { useProjectId } from "@/modules/projects/hooks/useProjectId";
import PageContent from "@/components/PageContent";
import SaveButton from "@/components/SaveButton";
import { CircleDollarSign } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useProjectQuery } from "@/modules/projects/hooks/useProjectQuery";
import { useProjectRateListQuery } from "@/modules/projects/components/rates/hooks/useProjectRateListQuery";
import { useToast } from "@/components/ui/use-toast";
import { generatePath, useNavigate } from "react-router-dom";
import { PATH_PROJECT_RATES } from "@/constants";
import ProjectRateFormFields from "@/modules/projects/components/rates/ProjectRateFormFields";
import { useModulePermissions } from "@/modules/account/hooks/useModulePermissions";
import { useCheckIsProjectLocked } from "../../hooks/useCheckIsProjectLocked";

function ProjectRateCreatorComponent() {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const projectId = useProjectId();
  const breadcrumbItems = useProjectRateBreadcrumbs();
  const projectQuery = useProjectQuery(projectId);
  const { listQuery } = useProjectRateListQuery();
  const project = projectQuery.data;
  const { can_create } = useModulePermissions();
  const isProjectLocked = useCheckIsProjectLocked();

  const {
    mutateAsync: createProjectRate,
    isPending: createProjectRatePending,
  } = useMutation({
    mutationFn: (values: WriteRoleBasedProjectRates) =>
      RoleBasedProjectRatesService.post(values),
    onSuccess: () => {
      toast({
        title: t("projectRates.success.create"),
      });
      navigate(generatePath(PATH_PROJECT_RATES, { projectId }));
    },
    onError: () => {
      toast({
        title: t("projectRates.errors.create"),
        variant: "destructive",
      });
    },
  });

  const isPending = listQuery.isLoading || createProjectRatePending;
  const form = useForm<WriteRoleBasedProjectRates>({
    resolver: zodResolver(writeRoleBasedProjectRates),
    defaultValues: {
      project_id: projectId,
    } as DefaultValues<WriteRoleBasedProjectRates>,
    disabled:
      createProjectRatePending ||
      listQuery.isLoading ||
      !can_create ||
      isProjectLocked,
  });

  useEffect(() => {
    if (project?.start_date) {
      const currentValues = form.getValues();
      form.reset({
        ...currentValues,
        valid_from: project.start_date,
      });
    }
  }, [project?.start_date, form]);

  useFormUnsavedChangesWarning(form);

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <ProjectDetailsAndNavigation />

      <Form className="mt-2" form={form} handleSubmit={createProjectRate}>
        <PageContent
          icon={<CircleDollarSign className="w-6 h-6 mr-3" />}
          title={t("projectRates.title.add")}
          footer={<SaveButton disabled={isProjectLocked} />}
        >
          <ProjectRateFormFields isPending={isPending} />
        </PageContent>
      </Form>
    </>
  );
}

export default memo(ProjectRateCreatorComponent);
